import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import * as AnalyticsActions from '../analytics.actions';
import * as AppEventsAnalyticsActions from '../app-events-analytics.actions';

import { CategoriesSelectors } from '@features/category';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { ConfigurationService } from '@shared/analytics/COMMON/services/configuration.service';
import { clickOnComponentCms } from '@shared/analytics/services/CMS/im-cms.service';
import { getCategoryPath } from '@shared/analytics/services/object-data/im-object-data.helper';
import { AttachmentsUtilsService } from '@shared/header/services/attachments.utils.service';

@Injectable()
export class AnalyticsCMSEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly configurationService: ConfigurationService,
        private readonly route: ActivatedRoute,
    ) { }

    public clickOnCMSComponent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.ClickOnCmsComponentAnalytics),
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            withLatestFrom(
                this.store.select(routerPublicQueries.getParamsFromRouter),
                this.store.select(routerPublicQueries.getDataFromRouter),
                this.store.select(CategoriesSelectors.getCategoriesNavigation),
                this.store.select(CategoriesSelectors.getMenuCategories),
            ),
            map(([action, params, routeData, categoriesNavigation, menuCategories]) => {
                const {
                    component, categoryId, linkType, id, linkSrc, parent,
                } = action;
                const sponsorcode = this.route.snapshot.queryParamMap.get('sponsorcode');

                let section: string;

                const pageName = params.view ?? routeData.pageName;

                switch (pageName) {
                    case 'ItxHomeMenPage':
                        section = 'home_men';
                        break;
                    case 'ItxHomeBgPage':
                        section = 'home_beauty';
                        break;
                    case 'ItxAngularLookbookPage':
                        section = 'lookbook';
                        break;

                    default:
                        section = 'home_women';
                        break;
                }

                let categoryPath: string = categoriesNavigation
                    ?.find((category) => category.id === categoryId)?.sectionPathEn?.toUpperCase();

                if (!categoryPath) {
                    const apiCategory = AttachmentsUtilsService.findCategory(
                        menuCategories,
                        categoryId
                    );

                    categoryPath = getCategoryPath(apiCategory)?.categoryPath;

                    if (!categoryPath && linkSrc) categoryPath = linkSrc;
                }

                return {
                    component,
                    categoryPath,
                    linkType,
                    id,
                    section,
                    parent,
                    sponsorcode,
                };
            }),
            map(({
                component, categoryPath, linkType, id, section, parent, sponsorcode,
            }) => {
                return {
                    data: clickOnComponentCms(component, categoryPath, linkType, section, id, parent, sponsorcode),
                    actionData: { component, linkType },
                };
            }),
            mergeMap(({ data, actionData }) => {
                const { eventData, extraData } = data;
                const { component, linkType } = actionData;
                return [
                    AnalyticsActions.SendCustomAnalytics({
                        eventData,
                        customData: extraData,
                    }),
                    AnalyticsActions.LoadEventSuccess({
                        payload: {
                            type: 'ClickOnHomeComponent',
                            dataObject: {
                                component, linkType,
                            },
                            eventFrom: EventFrom.home,
                        },
                    }),
                ];
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError((error: unknown) => {
                console.error(error);
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });
}
